import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import InfoPage from ".";
import './TravelerLogin.sass';
import { getReturnTo } from "../../utils/returnTo";
import { persistAuthResult } from "../../utils/authHelpers";

const TravelerLogin = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);

  // Check for session_expired reason in URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('reason') === 'session_expired') {
      setSessionExpired(true);
      // Clean up URL after showing message
      const cleanUrl = window.location.pathname;
      window.history.replaceState({}, '', cleanUrl);
    }
  }, [location.search]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      setErrorMessage("Please enter your email and password");
      return;
    }
    
    setSubmitting(true);
    setErrorMessage("");
    
    try {
      // Authenticate with email and password
      const loginRes = await fetch('/api/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      
      const loginData = await loginRes.json();
      
      if (loginRes.ok && loginData.ok) {
        persistAuthResult({ email, token: loginData.token, user: loginData.user });
        
        // Check if user has an active traveler subscription
        const res = await fetch('/api/access/subscription', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email }),
        });
        
        const data = await res.json();
        
        if (res.ok && data.ok && data.active && (data.roles || []).includes('traveler')) {
          // User has active traveler subscription, go to itineraries
          const returnTo = getReturnTo();
          navigate(returnTo || `/itineraries?email=${encodeURIComponent(email)}`);
        } else {
          // User logged in but no active traveler subscription, redirect to checkout
          const checkoutRes = await fetch('/api/traveler/checkout', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email }),
          });
          
          const checkoutData = await checkoutRes.json();
          
          if (checkoutData.ok && checkoutData.url) {
            window.location.href = checkoutData.url;
          } else {
            throw new Error('Failed to create checkout session');
          }
        }
      } else {
        setErrorMessage("Invalid email or password. Please try again.");
        setSubmitting(false);
      }
    } catch (error) {
      console.error('Login error:', error);
      setErrorMessage("Something went wrong. Please try again.");
      setSubmitting(false);
    }
  };

  const below = (
    <div className="traveler-login-container">
      <div className="traveler-login-content">
        <div className="traveler-login-left">
          <h1 className="traveler-login-title">
            TRAVELER LOG IN
          </h1>
          <p className="traveler-login-description">
            Access your curated itineraries and exclusive travel content.
          </p>
        </div>
        <div className="traveler-login-right">
          <div className="traveler-login-form-container">
            {sessionExpired && (
              <div style={{
                backgroundColor: '#fff3cd',
                border: '1px solid #ffc107',
                color: '#856404',
                padding: '12px 16px',
                borderRadius: '8px',
                marginBottom: '20px',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <span role="img" aria-label="warning">⚠️</span>
                Your session has expired. Please log in again.
              </div>
            )}
            <form onSubmit={handleSubmit} className="traveler-login-form">
              <div className="traveler-login-input-group">
                <input
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="traveler-login-input"
                  disabled={submitting}
                  required
                />
              </div>
              
              <div className="traveler-login-input-group">
                <div className="traveler-login-input-password-wrapper">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="traveler-login-input traveler-login-input-password"
                    disabled={submitting}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label="Toggle password visibility"
                    aria-pressed={showPassword}
                    className="traveler-login-password-toggle"
                  >
                    {showPassword ? (
                      <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M10.733 5.076a10.744 10.744 0 0 1 11.205 6.575 1 1 0 0 1 0 .696 10.747 10.747 0 0 1-1.444 2.49" />
                        <path d="M14.084 14.158a3 3 0 0 1-4.242-4.242" />
                        <path d="M17.479 17.499a10.75 10.75 0 0 1-15.417-5.151 1 1 0 0 1 0-.696 10.75 10.75 0 0 1 4.446-5.143" />
                        <path d="m2 2 20 20" />
                      </svg>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0" />
                        <circle cx="12" cy="12" r="3" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>
              
              {errorMessage && (
                <div className="traveler-login-error">{errorMessage}</div>
              )}
              
              <button
                type="submit"
                className="traveler-login-submit"
                disabled={submitting}
                style={{
                  backgroundColor: submitting ? '#ccc' : '#dc1d08'
                }}
              >
                {submitting ? "Logging in..." : "Log In"}
              </button>
            </form>
            
            <div className="traveler-login-signup-link">
              Don't have an account? <a href="/traveler-signup">Sign up</a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return <InfoPage below={below} disableStamps />;
};

export default TravelerLogin;